import React from 'react';
import styles from './Dashboard.module.scss';
import Table from '../../../Common/Table/Table/Table';
import useApiData from '../../../../../hooks/useApiData';
import Loading from '../../../Common/Loading/Loading';
import { getErrorComponentFromHttpError } from '../../../Common/Error/Error';
import ScoreDisplay from '../../../Common/ScoreDisplay/ScoreDisplay';
import { v4 as uuid } from 'uuid';

export default function Dashboard() {
  const [selectedTeams, setSelectedTeams] = React.useState([]);
  const {
    data: teamsData,
    error: teamsError,
    isLoading: teamsIsLoading,
  } = useApiData({
    path: `/administrator/teams`,
    params: {
      includeTeamMemberCounts: true,
      includeGoalCounts: true,
    },
  });
  const {
    data: cyclesData,
    error: cyclesError,
    isLoading: cyclesIsLoading,
  } = useApiData({
    path: `/administrator/cycles`,
  });

  React.useEffect(() => {
    setSelectedTeams((current) => {
      if ((current || []).length === 0 && (teamsData || []).length > 0) {
        return teamsData.map((t) => t.id);
      }
    });
  }, [teamsData]);

  const allTeamIds = React.useMemo(
    () => (Array.isArray(teamsData) ? teamsData.map((team) => team.id) : []),
    [teamsData],
  );

  if (teamsIsLoading || cyclesIsLoading) {
    return <Loading message="Fetching data ..." />;
  }

  if (teamsError || cyclesError || teamsData === null || cyclesData === null) {
    return getErrorComponentFromHttpError(teamsError || cyclesError);
  }

  const mappedTeamsData = (teamsData || []).map((team) => ({
    ...team,
    performanceScoreAllTime: <ScoreDisplay subjectType="team" scoreType="performance" teamId={team.id} />,
    performanceScore6Weeks: (
      <ScoreDisplay
        subjectType="team"
        scoreType="performance"
        teamId={team.id}
        fromDate={new Date(new Date().getTime() - 6 * 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
        toDate={new Date().toISOString().substring(0, 10)}
      />
    ),
    performanceScore1Week: (
      <ScoreDisplay
        subjectType="team"
        scoreType="performance"
        teamId={team.id}
        fromDate={new Date(new Date().getTime() - 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
        toDate={new Date().toISOString().substring(0, 10)}
      />
    ),
    processAdoptionScoreAllTime: <ScoreDisplay subjectType="team" scoreType="processAdoption" teamId={team.id} />,
    processAdoptionScore6Weeks: (
      <ScoreDisplay
        subjectType="team"
        scoreType="processAdoption"
        teamId={team.id}
        fromDate={new Date(new Date().getTime() - 6 * 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
        toDate={new Date().toISOString().substring(0, 10)}
      />
    ),
    processAdoptionScore1Week: (
      <ScoreDisplay
        subjectType="team"
        scoreType="processAdoption"
        teamId={team.id}
        fromDate={new Date(new Date().getTime() - 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
        toDate={new Date().toISOString().substring(0, 10)}
      />
    ),
  }));

  return (
    <div className={styles.dashboard}>
      <h1>Organisation</h1>
      <div className={styles.cards}>
        <div className={styles.card}>
          <div className={styles.row}>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  id={uuid()}
                  subjectType="organisation"
                  scoreType="performance"
                  teamIds={selectedTeams}
                  grow
                />
              </div>
              <h3>All time</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  subjectType="organisation"
                  scoreType="performance"
                  teamIds={selectedTeams}
                  fromDate={new Date(new Date().getTime() - 6 * 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past 6 weeks</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  subjectType="organisation"
                  scoreType="performance"
                  teamIds={selectedTeams}
                  fromDate={new Date(new Date().getTime() - 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past week</h3>
            </div>
          </div>
          <h2>Performance Score</h2>
        </div>
        <div className={styles.card}>
          <div className={styles.row}>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay scoreType="processAdoption" subjectType="organisation" teamIds={selectedTeams} grow />
              </div>
              <h3>All time</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  scoreType="processAdoption"
                  subjectType="organisation"
                  teamIds={selectedTeams}
                  fromDate={new Date(new Date().getTime() - 6 * 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past 6 weeks</h3>
            </div>
            <div className={styles.stack}>
              <div className={styles.score}>
                <ScoreDisplay
                  scoreType="processAdoption"
                  subjectType="organisation"
                  teamIds={selectedTeams}
                  fromDate={new Date(new Date().getTime() - 7 * 24 * 3600 * 1000).toISOString().substring(0, 10)}
                  toDate={new Date().toISOString().substring(0, 10)}
                  grow
                />
              </div>
              <h3>Past week</h3>
            </div>
          </div>
          <h2>Process Adoption Score</h2>
        </div>
      </div>

      <div className={styles.teamsTable}>
        <Table
          rows={mappedTeamsData}
          allowSort
          allowSelect
          onSelected={(selected) => setSelectedTeams(selected)}
          defaultSelected={allTeamIds}
          keyColumn="id"
          title="Teams"
          headers={[
            {
              colSpan: 3,
              label: null,
            },
            {
              colSpan: 3,
              label: 'Performance Score',
            },
            {
              colSpan: 3,
              label: 'Process Adoption Score',
            },
          ]}
          columns={[
            {
              id: 'label',
              label: 'Team',
              numeric: false,
            },
            {
              id: 'goalCount',
              label: 'Goals',
              numeric: false,
            },
            {
              id: 'teamMemberCount',
              label: 'Members',
              numeric: false,
            },
            {
              id: 'performanceScoreAllTime',
              label: 'All time',
              component: true,
              centered: true,
            },
            {
              id: 'performanceScore6Weeks',
              label: 'Past 6 weeks',
              component: true,
              centered: true,
            },
            {
              id: 'performanceScore1Week',
              label: 'Past week',
              component: true,
              centered: true,
            },
            {
              id: 'processAdoptionScoreAllTime',
              label: 'All time',
              component: true,
              centered: true,
            },
            {
              id: 'processAdoptionScore6Weeks',
              label: 'Past 6 weeks',
              component: true,
              centered: true,
            },
            {
              id: 'processAdoptionScore1Week',
              label: 'Past week',
              component: true,
              centered: true,
            },
          ]}
        />
      </div>
    </div>
  );
}
